import { Box } from "@mui/material";
import React, { useCallback, useContext, useEffect } from "react";
import { RendererContext } from "../contexts/rendererContext";
import { Pole } from "../../../sjorcraft3d/src/objects/pole";
import { SquareLashing } from "../../../sjorcraft3d/src/objects/lashings/squareLashing";
import { BipodLashing } from "../../../sjorcraft3d/src/objects/lashings/bipodLashing";
import { Scaffold } from "../../../sjorcraft3d/src/objects/scaffold";
import { useInventory } from "../hooks/useInventory";
import { usePoles } from "../hooks/usePoles";
import { useLashings } from "../hooks/useLashings";
import { PinwheelLoader } from "./PinwheelLoader";
import { ScaffoldLashing } from "../../../sjorcraft3d/src/objects/lashings/scaffoldLashing";
import { TripodLashing } from "../../../sjorcraft3d/src/objects/lashings/tripodLashing";

export const SjorcraftCanvas = () => {
  usePoles();
  useLashings();
  const {
    dbPoles,
    dbLashings,
    dbBipodLashings,
    dbTripodLashings,
    dbScaffoldLashings,
    poleset,
    projectLoading,
  } = useInventory();

  const renderer = useContext(RendererContext);
  const viewer = renderer.viewer;

  const syncPoleset = useCallback(() => {
    if (!viewer) return;
    const set = poleset.poles.map((p) => {
      return {
        length: p.length,
        color: parseInt(p.color.slice(1), 16),
        radius: p.radius,
      };
    });
    viewer.inventory.setPoleset(set);
  }, [viewer, poleset]);

  const syncPoles = useCallback(() => {
    if (!viewer) return;

    const viewerPoles = viewer.inventory.poles;
    const polesByIdentifier = new Map<any, any>(
      dbPoles.map((p) => [p.identifier, p])
    );

    // Check if poles in the scene need to be updated or removed
    for (const pole of viewerPoles) {
      const dbPole = polesByIdentifier[pole.identifier];
      if (!dbPole) {
        viewer.inventory.removePole(pole);
        continue;
      }

      pole.position.set(
        dbPole.position.x,
        dbPole.position.y,
        dbPole.position.z
      );
      pole.setDirection(
        pole.direction.set(
          dbPole.direction.x,
          dbPole.direction.y,
          dbPole.direction.z
        )
      );
      pole.setExactLength(dbPole.length);

      polesByIdentifier.delete(pole.identifier);
    }

    // The remaining poles need to be added to the scene
    for (const dbPole of polesByIdentifier.values()) {
      const pole = new Pole(dbPole.identifier);
      pole.position.set(
        dbPole.position.x,
        dbPole.position.y,
        dbPole.position.z
      );
      pole.setDirection(
        pole.direction.set(
          dbPole.direction.x,
          dbPole.direction.y,
          dbPole.direction.z
        )
      );
      pole.setExactLength(dbPole.length);
      viewer.inventory.pushPoles([pole]);
      viewer.scene.add(pole);
    }
  }, [viewer, dbPoles]);

  useEffect(() => {
    if (!viewer) return;
    syncPoleset();
    syncPoles();
    const viewerLashings = viewer.inventory.lashings;

    for (const lashing of viewerLashings) {
      viewer.inventory.removeSquareLashing(lashing);
    }

    for (const lashing of dbLashings) {
      const p1 = viewer.inventory.poles.find(
        (p) => p.identifier === lashing.fixedPoleIdentifier
      );
      const p2 = viewer.inventory.poles.find(
        (p) => p.identifier === lashing.loosePoleIdentifier
      );
      if (!p1 || !p2) continue;

      const l = new SquareLashing(lashing.identifier);
      l.setPropertiesFromTwoPoles(p1, p2);

      viewer.inventory.pushLashing(l);
      viewer.scene.add(l);
    }

    const viewerBipodLashings = viewer.inventory.bipodLashings;

    for (const lashing of viewerBipodLashings) {
      viewer.inventory.removeBipodLashing(lashing);
    }

    for (const lashing of dbBipodLashings) {
      const p1 = viewer.inventory.poles.find(
        (p) => p.identifier === lashing.pole1Identifier
      );
      const p2 = viewer.inventory.poles.find(
        (p) => p.identifier === lashing.pole2Identifier
      );
      if (!p1 || !p2) continue;

      const s1 = new Scaffold();
      s1.setMainPole(p1);

      const s2 = new Scaffold();
      s2.setMainPole(p2);

      const l = new BipodLashing(s1, s2, lashing.identifier);

      viewer.inventory.pushBipodLashing(l);
      viewer.scene.add(l);
    }

    const viewerTripodLashings = viewer.inventory.tripodLashings;

    for (const lashing of viewerTripodLashings) {
      viewer.inventory.removeTripodLashing(lashing);
    }

    for (const lashing of dbTripodLashings) {
      const p1 = viewer.inventory.poles.find(
        (p) => p.identifier === lashing.leftPoleIdentifier
      );
      const p2 = viewer.inventory.poles.find(
        (p) => p.identifier === lashing.middlePoleIdentifier
      );
      const p3 = viewer.inventory.poles.find(
        (p) => p.identifier === lashing.rightPoleIdentifier
      );
      if (!p1 || !p2 || !p3) continue;

      const s1 = new Scaffold();
      s1.setMainPole(p1);

      const s2 = new Scaffold();
      s2.setMainPole(p2);

      const s3 = new Scaffold();
      s3.setMainPole(p3);

      const l = new TripodLashing(s1, s2, s3, lashing.identifier);

      viewer.inventory.pushTripodLashing(l);
      viewer.scene.add(l);
    }

    const viewerScaffoldLashings = viewer.inventory.scaffoldLashings;

    for (const lashing of viewerScaffoldLashings) {
      viewer.inventory.removeScaffoldLashing(lashing);
    }

    for (const lashing of dbScaffoldLashings) {
      const p1 = viewer.inventory.poles.find(
        (p) => p.identifier === lashing.pole1Identifier
      );
      const p2 = viewer.inventory.poles.find(
        (p) => p.identifier === lashing.pole2Identifier
      );
      if (!p1 || !p2) continue;

      const l = new ScaffoldLashing(p1, p2, lashing.offset, lashing.identifier);

      viewer.inventory.pushScaffoldLashing(l);
      viewer.scene.add(l);
    }
  }, [
    dbPoles,
    dbLashings,
    dbBipodLashings,
    dbTripodLashings,
    dbScaffoldLashings,
    poleset,
    viewer,
    syncPoles,
  ]);

  return (
    <Box
      id="render_area"
      sx={{
        height: "100%",
        width: { xs: "100%", md: "calc(100% - 25rem)" },
        zIndex: 0,
        outline: "none",
        position: "relative",
      }}
    >
      {projectLoading && <PinwheelLoader />}
    </Box>
  );
};

export default SjorcraftCanvas;
